import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ApiService from '../services/api';
import { API_ENDPOINTS } from '../config/api';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const navigate = useNavigate();
  const codeRefs = useRef([]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const user = JSON.parse(localStorage.getItem('user_data') || 'null');
    if (token && user) {
      navigate('/treinamentos/cursos');
    }
  }, [navigate]);

  useEffect(() => {
    if (step === 2 && code.every(d => d !== '')) {
      document.getElementById('otp-form')?.requestSubmit();
    }
  }, [code, step]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await ApiService.post(API_ENDPOINTS.AUTH.REQUEST_CODE, { email });
      if (response.success) {
        setStep(2);
        setTimeout(() => codeRefs.current[0]?.focus(), 150);
      }
    } catch (err) {
      setError(err.message || 'Erro ao enviar código. Tente novamente.');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const codeStr = code.join('');
      const response = await ApiService.post(API_ENDPOINTS.AUTH.LOGIN, { email, code: codeStr });
      if (response.success) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
        navigate('/treinamentos/cursos', { state: { user: response.data.user } });
      }
    } catch (err) {
      setError(err.message || 'Código inválido. Verifique e tente novamente.');
      triggerShake();
      setCode(['', '', '', '', '', '']);
      setTimeout(() => codeRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
      codeRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) codeRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) codeRefs.current[index + 1]?.focus();
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    pasted.split('').forEach((digit, i) => { if (i < 6) newCode[i] = digit; });
    setCode(newCode);
    const nextEmpty = newCode.findIndex(d => d === '');
    codeRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
  };

  const handleBack = (e) => {
    e.preventDefault();
    setStep(1);
    setCode(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-brand-panel">
        <div className="brand-orb brand-orb-1" />
        <div className="brand-orb brand-orb-2" />
        <div className="brand-orb brand-orb-3" />

        <div className="brand-content">
          <div className="brand-logo">
            <div className="logo-icon-wrapper">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="brand-logo-svg" aria-label="Cloud Cafe">
                {/* Steam — 3 linhas onduladas */}
                <path d="M14 15 C13 12 15 10 14 7" stroke="rgba(255,255,255,0.72)" strokeWidth="1.9" strokeLinecap="round"/>
                <path d="M24 13 C23 10 25 8 24 5"  stroke="rgba(255,255,255,0.72)" strokeWidth="1.9" strokeLinecap="round"/>
                <path d="M34 15 C33 12 35 10 34 7" stroke="rgba(255,255,255,0.72)" strokeWidth="1.9" strokeLinecap="round"/>
                {/* Corpo da xícara (trapézio) */}
                <path d="M8 17 L11 37 H37 L40 17 Z" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.9)" strokeWidth="1.9" strokeLinejoin="round"/>
                {/* Superfície do café */}
                <ellipse cx="24" cy="17" rx="16" ry="3.5" fill="rgba(80,28,6,0.88)" stroke="rgba(255,255,255,0.22)" strokeWidth="1"/>
                {/* Alça */}
                <path d="M38 22 Q44 22 44 28 Q44 33 38 33" stroke="rgba(255,255,255,0.9)" strokeWidth="1.9" fill="none" strokeLinecap="round"/>
                {/* Pires */}
                <ellipse cx="24" cy="39" rx="19" ry="3.5" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.58)" strokeWidth="1.6"/>
              </svg>
            </div>
            <span className="brand-logo-text">Cloud Cafe & DevOps</span>
          </div>

          <div className="brand-tagline">
            <h1>Aprenda.<br />Evolua.<br />Transforme.</h1>
            <p>Cursos de cloud, DevOps e tecnologia para levar sua carreira ao próximo nível.</p>
          </div>

          <div className="brand-features">
            <div className="brand-feature">
              <span className="feature-icon">☁️</span>
              <span>Cloud Computing & AWS</span>
            </div>
            <div className="brand-feature">
              <span className="feature-icon">⚙️</span>
              <span>DevOps & CI/CD Pipelines</span>
            </div>
            <div className="brand-feature">
              <span className="feature-icon">🔐</span>
              <span>Segurança & Infraestrutura</span>
            </div>
          </div>
        </div>
      </div>

      <div className="login-form-panel">
        <div className={`login-card ${shake ? 'shake' : ''}`}>
          <div className="login-card-header">
            <div className="step-indicator">
              <div className={`step-dot ${step >= 1 ? 'active' : ''}`} />
              <div className="step-line" />
              <div className={`step-dot ${step >= 2 ? 'active' : ''}`} />
            </div>
          </div>

          {step === 1 ? (
            <div className="form-section" key="step1">
              <div className="form-header">
                <h2>Bem-vindo de volta!</h2>
                <p>Digite seu e-mail para receber o código de acesso</p>
              </div>

              {error && <div className="error-banner">{error}</div>}

              <form onSubmit={handleSendCode}>
                <div className="input-group">
                  <label htmlFor="email">E-mail</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}>
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>

                <button type="submit" className="btn-login" disabled={loading}>
                  {loading ? (
                    <span className="btn-loading">
                      <span className="spinner" />
                      Enviando...
                    </span>
                  ) : (
                    <>Enviar código de acesso <span className="btn-arrow">→</span></>
                  )}
                </button>
              </form>

              <div className="form-footer">
                <p>Não tem conta? <Link to="/treinamentos/cadastro">Cadastre-se gratuitamente</Link></p>
              </div>
            </div>
          ) : (
            <div className="form-section" key="step2">
              <div className="form-header">
                <h2>Verifique seu e-mail</h2>
                <p>Enviamos um código de 6 dígitos para<br /><strong>{email}</strong></p>
              </div>

              {error && <div className="error-banner">{error}</div>}

              <form id="otp-form" onSubmit={handleLogin}>
                <div className="otp-group">
                  <label>Código de verificação</label>
                  <div className="otp-inputs" onPaste={handleCodePaste}>
                    {code.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => codeRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={e => handleCodeChange(index, e.target.value)}
                        onKeyDown={e => handleCodeKeyDown(index, e)}
                        className={`otp-input ${digit ? 'filled' : ''}`}
                        disabled={loading}
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-login"
                  disabled={loading || code.join('').length !== 6}
                >
                  {loading ? (
                    <span className="btn-loading">
                      <span className="spinner" />
                      Verificando...
                    </span>
                  ) : (
                    <>Entrar <span className="btn-arrow">→</span></>
                  )}
                </button>
              </form>

              <div className="form-footer">
                <p>Não recebeu o código? <button className="link-btn" onClick={handleSendCode}>Reenviar</button></p>
                <p><button className="link-btn" onClick={handleBack}>← Usar outro e-mail</button></p>
              </div>
            </div>
          )}
        </div>

        <p className="login-copyright">© 2026 Cloud Cafe & DevOps · Todos os direitos reservados</p>
      </div>
    </div>
  );
}

export default Login;
